/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, ElementRef, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ColDef, GridReadyEvent, SizeColumnsToContentStrategy} from 'ag-grid-community';
import {environment} from '../../../environments/environment';
import {IcdSearchResult} from '../../model/icdSearchResult.model';
import {
    ActiveFilter,
    ColumnFilterModel,
    describeActiveFilters,
    filterModelValues
} from '../../utils/active-filters.util';
import {UrlCellRendererComponent} from '../cell-renderer/url-cell-renderer.component';
import {EditableGridComponent} from '../editable-grid-component';
import {
    CustomMultiSelectFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-filter.component';
import {
    CustomMultiSelectFloatingFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-floating-filter.component';
import {
    NumericListFloatingFilterComponent
} from '../filter-components/numeric-list-floating-filter/numeric-list-floating-filter.component';
import {ServerPagingController} from '../server-paging/server-paging-controller';
import {CommonHelperService} from '../../service/common-helper.service';

/** What the server sends in place of a value the user's filter group hides. */
const RESTRICTED = 'RESTRICTED';

/** One ICD as the grid shows it, flattened from the server's ICDInfo the way the legacy ICD results grid does. */
export interface IcdReportingRow {
    icdID: number;
    icdName: string;
    icdType: string;
    parentIndicator: string;
    icdSourceID: number | null;
    deviation: string;
    icdSubType: string;
    stage: number | string;
    status: string;
    program: string;
    evClassification: string;
    subprograms: string;
    modelYear: number | string;
    modelYears: string;
    milestone: string;
    deviceName: string;
    deviceVariantDesc: string;
    objectCode: string;
    childObjectCode: string;
    gpsProjectNumber: string;
    dyno: boolean;
    isEioIcd: boolean;
    operateNormally: string;
    fullyFunctional: string;
    keyword: string;
    gmPartNumber: string;
    dreName: string;
    delegateDreName: string;
    electricalArchitectName: string;
    delegateElectricalArchitectName: string;
    deviceVseName: string;
    electricalVseName: string;
    deviceEgmName: string;
    architectEgmName: string;
    icdEngineerName: string;
    upc: string;
    fna: string;
    vpps: string;
    deviceRPO: string | null;
    url: string;
    architecture: string;
    rcdInterfIdentifiedDate: string;
    rcdConnDate: string;
    rcdInterfAgreedDate: string;
    rcdIverDate: string;
    rcdSopDate: string;
    icdSourced: string;
    icdSourcedDate: string;
    diodeIFSM: number;
    diodeVRRM: number;
}

/** The legacy grid's hints on its numeric filters, which match a typed number within or at the start of a value. */
const CONTAINS_HINT = 'contains...';
const STARTS_WITH_HINT = 'starts with...';

/** How a refused search reports itself; the reason may sit in any of these, as the other eSCI Tools grids read it. */
type SearchError = { error?: { responseMessage?: string, error?: string, message?: string }, message?: string };

type FilterOption = { value: string, label: string };
const YES_NO: FilterOption[] = [{value: 'Y', label: 'Yes'}, {value: 'N', label: 'No'}];
const TRUE_FALSE: FilterOption[] = [{value: 'Y', label: 'True'}, {value: 'N', label: 'False'}];
const YES_NO_NA: FilterOption[] = [...YES_NO, {value: 'NA', label: 'Not Applicable'}];
const COMPONENT_SOURCED: FilterOption[] = [...YES_NO, {value: 'C', label: 'Carry Over'}];

/**
 * A read-only, server-paged ICD search. Every filter and sort runs in the same search as the legacy ICD results grid,
 * so program restrictions apply; each column's id is the parameter or sort key that search knows it by.
 */
@Component({
    selector: 'app-icd-reporting',
    templateUrl: './icd-reporting.component.html',
    styleUrls: ['./icd-reporting.component.scss'],
    standalone: false
})
export class IcdReportingComponent extends EditableGridComponent implements OnInit, OnDestroy {
    permissionUrl = '/esciUI/icdReportingPermission';

    readonly paging = new ServerPagingController<IcdSearchResult>({
        fetchPage: request => {
            this.showLoadingOverlay();
            return this.esciService.searchIcdReporting(request);
        },
        collectCriteria: () => this.collectCriteria(),
        onPage: rows => {
            this.setRowData(rows.map(IcdReportingComponent.toRow));
            this.hideLoadingOverlay(rows.length === 0);
        },
        onError: err => {
            const refused = err as SearchError | null;
            this.clearRejectedSearch();
            this.messageService.processError(
                refused?.error?.responseMessage || refused?.error?.error || refused?.error?.message ||
                refused?.message || 'Unable to search ICDs.');
        }
    });

    /**
     * One line per cell: each page sizes its columns to what it shows instead (see onRowDataUpdated). That sizing sees
     * only the rows and columns drawn at the time, so a longer value drawn later on scroll is clipped, not overlapped.
     * The comparator keeps each page in the server's order: the server sorts on the stored value (a DRE's GMIN, not
     * the name shown), and that order is what cut the pages.
     */
    override defaultColDef: ColDef = {
        ...this.defaultColDef, editable: false, wrapText: false, minWidth: 90, comparator: () => 0,
        cellStyle: () => ({display: 'flex', alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis'}),
        tooltipValueGetter: params => params.valueFormatted ?? params.value
    };
    override autoSizeStrategy: SizeColumnsToContentStrategy = {type: 'fitCellContents'};
    readonly activeFilters = signal<ActiveFilter[]>([]);
    /** Set while the sort arrow is cleared after a refusal, so that clear does not start another search. */
    private suppressSortReload = false;
    private readonly host = inject(ElementRef);

    /**
     * The legacy ICD results grid's columns (searchICDController.js), in its order and with its default visibility.
     * Each dropdown takes one value at a time, as the legacy grid's select filters do. A column is sortable only where
     * the legacy grid sorts it and the server search can order by it.
     */
    override cols: ColDef[] = [
        {
            field: 'icdID', headerName: 'ICD ID', initialSort: 'desc',
            ...IcdReportingComponent.numericListFilter(CONTAINS_HINT),
            cellRenderer: UrlCellRendererComponent,
            cellRendererParams: {urlBase: IcdReportingComponent.uiBaseUrl() + '/ui/icd/${icdID}'}
        },
        {field: 'icdName', headerName: 'ICD Name', ...IcdReportingComponent.textFilter()},
        {field: 'icdType', headerName: 'ICD Type', ...this.dropdownFilter('icdTypes')},
        {
            field: 'parentIndicator', headerName: 'Parent ICD', sortable: false,
            ...IcdReportingComponent.fixedDropdownFilter(YES_NO)
        },
        {
            field: 'icdSourceID', headerName: 'Source ICD', sortable: false,
            ...IcdReportingComponent.numericListFilter(STARTS_WITH_HINT),
            cellRenderer: UrlCellRendererComponent,
            cellRendererParams: {urlBase: IcdReportingComponent.uiBaseUrl() + '/ui/icd/${icdSourceID}'}
        },
        {
            field: 'deviation', headerName: 'Difference', sortable: false,
            ...IcdReportingComponent.fixedDropdownFilter(YES_NO)
        },
        {field: 'icdSubType', headerName: 'ICD Subtype', ...this.dropdownFilter('icdSubtype')},
        {
            field: 'stage', headerName: 'Stage',
            ...IcdReportingComponent.numericListFilter("Stage # (ie. '0', '1' or '0,4')")
        },
        {field: 'status', headerName: 'Status', ...this.dropdownFilter('status')},
        {field: 'program', headerName: 'Program', ...IcdReportingComponent.textFilter()},
        {
            field: 'evClassification', headerName: 'EV Classification', sortable: false,
            ...this.dropdownFilter('evClassifications')
        },
        {field: 'subprograms', headerName: 'Sub Programs', ...IcdReportingComponent.textFilter()},
        {field: 'modelYear', headerName: 'Model Year', ...IcdReportingComponent.numericListFilter(CONTAINS_HINT)},
        {field: 'modelYears', headerName: 'Model Years', ...IcdReportingComponent.numericListFilter(CONTAINS_HINT)},
        {field: 'milestone', headerName: 'Milestone', ...this.dropdownFilter('buildStatus')},
        {field: 'deviceName', headerName: 'Device Name', ...IcdReportingComponent.textFilter()},
        {field: 'deviceVariantDesc', headerName: 'Device Variant Desc', ...IcdReportingComponent.textFilter()},
        {field: 'objectCode', headerName: 'Object Code', ...IcdReportingComponent.textFilter()},
        {field: 'childObjectCode', headerName: 'Child Object Code', ...IcdReportingComponent.textFilter()},
        {field: 'gpsProjectNumber', headerName: 'GPS Project Number', ...IcdReportingComponent.textFilter()},
        {field: 'dyno', headerName: 'Dyno', ...IcdReportingComponent.fixedDropdownFilter(TRUE_FALSE)},
        {
            field: 'isEioIcd', headerName: 'Is EIO ICD', sortable: false,
            ...IcdReportingComponent.fixedDropdownFilter(TRUE_FALSE)
        },
        {
            field: 'operateNormally', headerName: 'Operates in A/Stop',
            ...IcdReportingComponent.fixedDropdownFilter(YES_NO_NA)
        },
        {
            field: 'fullyFunctional', headerName: 'Full Func. in A/Stop',
            ...IcdReportingComponent.fixedDropdownFilter(YES_NO_NA)
        },
        {field: 'keyword', headerName: 'Keyword', ...IcdReportingComponent.textFilter()},
        {field: 'gmPartNumber', headerName: 'GM Part Number', ...IcdReportingComponent.textFilter()},
        {colId: 'dre_a.fullName', field: 'dreName', headerName: 'Device DRE', filter: false},
        {colId: 'delegate_dre_a.fullName', field: 'delegateDreName', headerName: 'Delegate Device DRE', filter: false},
        {colId: 'dre_b.fullName', field: 'electricalArchitectName', headerName: 'Electrical Architect', filter: false},
        {
            colId: 'delegate_dre_b.fullName', field: 'delegateElectricalArchitectName',
            headerName: 'Delegate Electrical Architect', filter: false
        },
        {colId: 'vse_a.fullName', field: 'deviceVseName', headerName: 'Device VSE', filter: false},
        {colId: 'vse_b.fullName', field: 'electricalVseName', headerName: 'Electrical VSE', filter: false},
        {colId: 'egm_a.fullName', field: 'deviceEgmName', headerName: 'Device EGM', filter: false},
        {colId: 'egm_b.fullName', field: 'architectEgmName', headerName: 'Architect EGM', filter: false},
        {colId: 'icd_engr.fullName', field: 'icdEngineerName', headerName: 'ICD Engineer', filter: false},
        {field: 'upc', headerName: 'UPC', sortable: false, ...IcdReportingComponent.textFilter()},
        {field: 'fna', headerName: 'FNA', sortable: false, ...IcdReportingComponent.textFilter()},
        {field: 'vpps', headerName: 'VPPS', sortable: false, ...IcdReportingComponent.textFilter()},
        {field: 'deviceRPO', headerName: 'Device RPO', sortable: false, ...IcdReportingComponent.textFilter()},
        {field: 'url', headerName: 'URL', ...IcdReportingComponent.textFilter()},
        {field: 'architecture', headerName: 'Architecture', sortable: false, ...this.dropdownFilter('platforms')},
        {field: 'rcdInterfIdentifiedDate', headerName: 'Stage 0 Due Date', ...IcdReportingComponent.dateFilter()},
        {field: 'rcdConnDate', headerName: 'Stage 1 Due Date', ...IcdReportingComponent.dateFilter()},
        {field: 'rcdInterfAgreedDate', headerName: 'Stage 2 Due Date', ...IcdReportingComponent.dateFilter()},
        {field: 'rcdIverDate', headerName: 'Stage 3 Due Date', ...IcdReportingComponent.dateFilter()},
        {field: 'rcdSopDate', headerName: 'Stage 4 Due Date', ...IcdReportingComponent.dateFilter()},
        {
            field: 'icdSourced', headerName: 'Component Sourced', sortable: false,
            ...IcdReportingComponent.fixedDropdownFilter(COMPONENT_SOURCED)
        },
        {field: 'icdSourcedDate', headerName: 'Component Sourced Date', ...IcdReportingComponent.dateFilter()},
        {
            field: 'diodeIFSM', headerName: 'Diode IFSM (Amps)',
            ...IcdReportingComponent.numericListFilter(STARTS_WITH_HINT, true)
        },
        {
            field: 'diodeVRRM', headerName: 'Diode VRRM (Volts)',
            ...IcdReportingComponent.numericListFilter(STARTS_WITH_HINT, true)
        }
    ];

    ngOnInit(): void {
        // ag-grid copies its options when the grid is created, so a later change would never reach the overlay.
        const message = () => 'Loading ICDs...';
        this.gridOptions.loadingOverlayComponent = 'customNoRowsOverlay';
        this.gridOptions.loadingOverlayComponentParams =
            {message: message(), messageGetter: message, isLoadingGetter: () => true};
        this.esciService.checkPermission(this);
    }

    ngOnDestroy(): void {
        this.paging.destroy();
    }

    /** The grid exists only once permission is granted, so this is the first moment a search is allowed. */
    protected override afterPrimaryGridReady(_params: GridReadyEvent): void {
        this.paging.load(true);
    }

    override onFilterChanged(): void {
        if (this.paging.loading()) {
            // The base would swap the loading message for "no ICDs match" over rows that are about to be replaced.
            this.updateActiveFilters();
        } else {
            super.onFilterChanged();
        }
        this.paging.scheduleFilterRefresh();
    }

    onSortChanged(): void {
        if (this.suppressSortReload || !this.isGridApiAlive(this.gridApi)) {
            return;
        }
        const primary = (this.gridApi.getColumnState() || []).filter(state => !!state.sort)
                            .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))[0];
        this.paging.setSort(primary?.colId ?? null, primary?.sort === 'desc' ? 'desc' : 'asc');
    }

    protected override getDynamicNoRowsMessage(): string {
        return 'No ICDs match these filters.';
    }

    override updateActiveFilters(): void {
        const filterModel = this.isGridApiAlive(this.gridApi) ? this.gridApi.getFilterModel() : null;
        this.activeFilters.set(describeActiveFilters(filterModel,
                                                     colId => this.gridApi.getColumnDef(colId)?.headerName));
    }

    /** Sizes each column to the page it shows; content narrower than the grid is stretched to fill it. */
    onRowDataUpdated(): void {
        // Deferred so ag-Grid has rendered the new rows before they are measured.
        setTimeout(() => {
            if (!this.isGridApiAlive(this.gridApi)) {
                return;
            }
            this.gridApi.autoSizeAllColumns(false);
            const contentWidth = (this.gridApi.getAllDisplayedColumns() || [])
                .reduce((width, col) => width + col.getActualWidth(), 0);
            if (contentWidth < this.gridViewportWidth()) {
                this.gridApi.sizeColumnsToFit();
            }
        }, 0);
    }

    /**
     * Covers the rows on screen while the page replacing them is read, as the component grid does. The no-rows overlay
     * component carries the text, since AG Grid's loading template is injected as raw HTML.
     */
    /** A refused search must not keep showing the previous page or the sort the server rejected. */
    private clearRejectedSearch(): void {
        this.paging.abandonRejectedSearch();
        this.setRowData([]);
        this.suppressSortReload = true;
        try {
            if (!this.isGridApiAlive(this.gridApi)) {
                return;
            }
            this.gridApi.applyColumnState({defaultState: {sort: null}});
            this.gridApi.hideOverlay();
        } finally {
            this.suppressSortReload = false;
        }
    }

    private showLoadingOverlay(): void {
        if (!this.isGridApiAlive(this.gridApi)) {
            return;
        }
        this.gridApi.showLoadingOverlay();
    }

    private hideLoadingOverlay(empty: boolean): void {
        if (!this.isGridApiAlive(this.gridApi)) {
            return;
        }
        this.gridApi.hideOverlay();
        if (empty) {
            this.gridApi.showNoRowsOverlay();
        }
    }

    private gridViewportWidth(): number {
        return this.host.nativeElement.querySelector('.ag-body-viewport')?.clientWidth ?? 0;
    }

    private collectCriteria(): Record<string, string[]> {
        const criteria: Record<string, string[]> = {};
        const filterModel: Record<string, ColumnFilterModel> =
            this.isGridApiAlive(this.gridApi) ? (this.gridApi.getFilterModel() || {}) : {};
        for (const [colId, model] of Object.entries(filterModel)) {
            const values = filterModelValues(model);
            if (values.length > 0) {
                criteria[colId] = values;
            }
        }
        return criteria;
    }

    private dropdownFilter(key: string): ColDef {
        return {
            filter: CustomMultiSelectFilterComponent,
            floatingFilterComponent: CustomMultiSelectFloatingFilterComponent,
            filterParams: {
                serverSideFiltering: true,
                distinctValuesProvider: () => this.esciService.getIcdReportingFilterValues(key),
                maxSelections: 1
            }
        };
    }

    /** A legacy fixed select (Yes/No and the like): offered as listed, matched on its code by the server. */
    private static fixedDropdownFilter(fixedOptions: FilterOption[]): ColDef {
        return {
            filter: CustomMultiSelectFilterComponent,
            floatingFilterComponent: CustomMultiSelectFloatingFilterComponent,
            filterParams: {serverSideFiltering: true, fixedOptions, maxSelections: 1}
        };
    }

    /**
     * The server matches a date column on one whole day, so "equals" is the only comparison offered. Every row is
     * treated as that day: the page on screen is already the server's answer to the filter.
     */
    private static dateFilter(): ColDef {
        return {
            sortable: false, filter: 'agDateColumnFilter',
            filterParams: {filterOptions: ['equals'], maxNumConditions: 1, comparator: () => 0}
        };
    }

    /**
     * The search matches typed text within a value, so "contains" is the only comparison offered. Every row matches
     * here: the page on screen is already the server's answer to the filter.
     */
    private static textFilter(): ColDef {
        return {
            filter: 'agTextColumnFilter',
            filterParams: {filterOptions: ['contains'], maxNumConditions: 1, textMatcher: () => true}
        };
    }

    /**
     * Digits and commas only, as on the legacy ICD search; a comma list matches any of the numbers. With `decimal`,
     * each number may carry a decimal point. The parent text filter's own popup is hidden because its input cannot be
     * restricted.
     */
    private static numericListFilter(placeholder: string, decimal = false): ColDef {
        return {
            ...IcdReportingComponent.textFilter(),
            floatingFilterComponent: NumericListFloatingFilterComponent,
            suppressFloatingFilterButton: true,
            suppressHeaderFilterButton: true,
            filterParams: {
                ...IcdReportingComponent.textFilter().filterParams, filterPlaceholder: placeholder,
                ...(decimal ? {decimal} : {})
            }
        };
    }

    /** The legacy JSP pages live on the backend host, not under the esci-tools route. */
    private static uiBaseUrl(): string {
        return String(environment.apiUrl || window.location.origin).replace(/\/+$/, '');
    }

    /**
     * Mirrors the legacy grid's convertResponseToData, so each cell reads the same as it does there. The search
     * HTML-escapes every string it returns and the grid draws cells as text, so each one is decoded once here.
     */
    private static toRow(icd: IcdSearchResult): IcdReportingRow {
        const row = IcdReportingComponent.toEscapedRow(icd) as unknown as Record<string, unknown>;
        for (const [field, value] of Object.entries(row)) {
            if (typeof value === 'string' && value.includes('&')) {
                row[field] = CommonHelperService.htmlDecode(value);
            }
        }
        return row as unknown as IcdReportingRow;
    }

    private static toEscapedRow(icd: IcdSearchResult): IcdReportingRow {
        const metadata = icd?.icdMetadata;
        return {
            icdID: icd?.id,
            icdName: icd?.icdName,
            icdType: icd?.icdType,
            parentIndicator: icd?.parentIndicator === true ? 'Yes' : 'No',
            icdSourceID: icd?.icdSourceID > 0 ? icd.icdSourceID : null,
            deviation: icd?.deviationComment && icd.deviationComment !== RESTRICTED ? 'Yes' : 'No',
            icdSubType: icd?.icdSubType,
            stage: icd?.stage?.stageVal,
            status: icd?.status,
            program: icd?.program?.programName,
            evClassification: icd?.evClassification ?? '',
            subprograms: (icd?.icdCommonProgramList ?? []).map(p => p?.subProgram?.subProgramName ?? '')
                                                           .sort((a, b) => a.localeCompare(b)).join(':'),
            modelYear: icd?.modelYear?.year,
            modelYears: (icd?.modelYears ?? []).map(year => String(year).trim())
                                               .sort((a, b) => a.localeCompare(b)).join(', '),
            milestone: metadata?.buildStatus?.buildStatus,
            deviceName: icd?.deviceName,
            deviceVariantDesc: icd?.deviceVariantDesc,
            objectCode: icd?.objectCode?.code,
            childObjectCode: icd?.childObjectCodes,
            gpsProjectNumber: icd?.gpsProjectNumber,
            dyno: icd?.isDynoICD,
            isEioIcd: icd?.isEioIcd,
            operateNormally: metadata?.operateNormally,
            fullyFunctional: metadata?.fullyFunctional,
            keyword: icd?.keyword,
            gmPartNumber: icd?.gmPartNumber,
            dreName: metadata?.dreUsers?.userA?.fullName,
            delegateDreName: metadata?.dreDelegateUsers?.userA?.fullName,
            electricalArchitectName: metadata?.dreUsers?.userB?.fullName,
            delegateElectricalArchitectName: metadata?.dreDelegateUsers?.userB?.fullName,
            deviceVseName: metadata?.vseUsers?.userA?.fullName,
            electricalVseName: metadata?.vseUsers?.userB?.fullName,
            deviceEgmName: metadata?.egmUsers?.userA?.fullName,
            architectEgmName: metadata?.egmUsers?.userB?.fullName,
            icdEngineerName: metadata?.icdEngineer?.fullName,
            upc: icd?.upc?.upcCode,
            fna: icd?.fna?.fnaCode,
            vpps: icd?.vpps?.vppsAndDesc,
            deviceRPO: icd?.option?.optionCode ?? null,
            url: metadata?.spLocation,
            architecture: metadata?.platform,
            rcdInterfIdentifiedDate: metadata?.rcdInterfIdentifiedDate,
            rcdConnDate: metadata?.rcdConnDate,
            rcdInterfAgreedDate: metadata?.rcdInterfAgreedDate,
            rcdIverDate: metadata?.rcdIverDate,
            rcdSopDate: metadata?.rcdSopDate,
            icdSourced: icd?.icdSourced,
            icdSourcedDate: icd?.icdSourcedDate,
            diodeIFSM: metadata?.diodeIFSM,
            diodeVRRM: metadata?.diodeVRRM
        };
    }
}
